/**
 * User Service
 * Handles user registration and profile management
 */

import { httpClient, ApiResponse } from './http.client';
import { getApiEndpoint } from '../config/config.loader';
import { emailActivationService } from './email-activation.service';

export interface RegisterUserData {
  // Company Information
  companyName: string;
  companyType?: string;
  industry?: string;
  companySize?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  currency?: string;
  timezone?: string;
  website?: string;
  
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  
  // Account Setup (password removed as it's not needed for new API)
  agreeToTerms: boolean;
  agreeToMarketing?: boolean;
}

// Enums for backend mapping
export const CompanySizeEnum = {
  '1-10': 'STARTUP',
  '11-50': 'SMALL',
  '51-200': 'MEDIUM', 
  '201-500': 'LARGE',
  '500+': 'ENTERPRISE'
} as const;

export const CompanyTypeEnum = {
  'general-contractor': 'GENERAL_CONTRACTOR',
  'subcontractor': 'SUBCONTRACTOR', 
  'architecture': 'ARCHITECTURE_FIRM',
  'architect': 'ARCHITECTURE_FIRM',
  'engineering': 'ENGINEERING_FIRM',
  'construction-management': 'CONSTRUCTION_MANAGEMENT',
  'developer': 'DEVELOPER',
  'supplier': 'SUPPLIER',
  'consultant': 'CONSULTANT',
  'other': 'OTHER'
} as const;

export interface UserRegistrationResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  message: string;
}

export interface UserProfile {
  // Core Identity
  user_id: string;
  username: string;
  email: string;
  
  // Personal Information
  first_name: string;
  last_name: string;
  full_name?: string;
  phone?: string;
  
  // Job Information
  job_title?: string;
  department?: string;
  hire_date?: string;
  employee_id?: string;
  
  // Organization Relationship
  organization_id?: string;
  
  // Access Control
  roles: string[];
  permissions: string[];
  status: string;
  
  // Emergency Contact
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  
  // Profile Settings
  profile_picture_url?: string;
  timezone?: string;
  language?: string;
  
  // Activity Tracking
  last_login?: string;
  login_count?: number;
  project_count?: number;
  
  // System Metadata
  is_active: boolean;
  email_verified: boolean;
  two_factor_enabled: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
}

class UserService {
  /**
   * Register a new user and organization
   */
  async registerUser(userData: RegisterUserData): Promise<ApiResponse<UserRegistrationResponse>> {
    try {
      // Validate required fields
      this.validateRegistrationData(userData);

      // Map company size to backend enum
      const companySize = userData.companySize ? CompanySizeEnum[userData.companySize as keyof typeof CompanySizeEnum] || 'OTHER' : 'STARTUP';
      
      // Map company type to backend enum  
      const companyType = userData.companyType ? CompanyTypeEnum[userData.companyType as keyof typeof CompanyTypeEnum] || 'OTHER' : 'GENERAL_CONTRACTOR';

      // Transform data to match new backend API format
      const registrationPayload = {
        organization: {
          name: userData.companyName,
          company_type: companyType,
          company_size: companySize,
          industry: userData.industry || 'Construction',
          website: userData.website || null,
          address: userData.address || '',
          city: userData.city || '',
          state: userData.state || '',
          country: userData.country || 'US',
          currency: userData.currency || 'USD',
          timezone: userData.timezone || 'UTC'
        },
        user: {
          email: userData.email,
          first_name: userData.firstName,
          last_name: userData.lastName,
          job_title: userData.jobTitle || ''
        },
        terms_accepted: userData.agreeToTerms,
        marketing_consent: userData.agreeToMarketing || false
      };

      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Registration payload:', registrationPayload);
      }

      const endpoint = getApiEndpoint('registration');
      const response = await httpClient.post<UserRegistrationResponse>(
        endpoint,
        registrationPayload,
        { requireAuth: false }
      );

      // Automatically send activation email after successful registration
      if (response.success) {
        try {
          await emailActivationService.sendActivationEmail({
            email: userData.email,
            user_id: response.data.id,
            template: 'welcome'
          });
          if (process.env.NODE_ENV === 'development') {
            console.log('Activation email sent successfully');
          }
        } catch (emailError) {
          console.warn('Failed to send activation email:', emailError);
          // Don't fail the registration if email sending fails
        }
      }

      return response;
    } catch (error) {
      console.error('User registration failed:', error);
      throw error;
    }
  }

  /**
   * Get user profile by ID
   */
  async getUserProfile(userId?: string): Promise<ApiResponse<UserProfile>> {
    const endpoint = userId ? `/api/v1/users/${userId}` : '/api/v1/users/me';
    const response = await httpClient.get<UserProfile>(endpoint);
    
    // Transform backend response to frontend format if needed
    if (response.success && response.data) {
      response.data = this.transformUserProfile(response.data);
    }
    
    return response;
  }

  /**
   * Get available roles
   */
  async getAvailableRoles(): Promise<ApiResponse<string[]>> {
    return httpClient.get<string[]>('/api/v1/users/roles/available');
  }

  /**
   * Get user preferences
   */
  async getUserPreferences(): Promise<ApiResponse<any>> {
    return httpClient.get<any>('/api/v1/users/preferences');
  }

  /**
   * Update user preferences
   */
  async updateUserPreferences(preferences: any): Promise<ApiResponse<any>> {
    return httpClient.put<any>('/api/v1/users/preferences', preferences);
  }

  /**
   * Reset user preferences
   */
  async resetUserPreferences(): Promise<ApiResponse<void>> {
    return httpClient.post<void>('/api/v1/users/preferences/reset', {});
  }

  /**
   * Upload user avatar
   */
  async uploadAvatar(avatarData: FormData): Promise<ApiResponse<any>> {
    return httpClient.post<any>('/api/v1/users/upload-avatar', avatarData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Activate user
   */
  async activateUser(userId: string): Promise<ApiResponse<any>> {
    return httpClient.post<any>(`/api/v1/users/${userId}/activate`, {});
  }

  /**
   * Update user profile
   */
  async updateUserProfile(userId: string, profileData: Partial<UserProfile>): Promise<ApiResponse<UserProfile>> {
    // Transform frontend data to backend format
    const backendData = this.transformToBackendFormat(profileData);
    const response = await httpClient.put<UserProfile>(`/api/v1/users/${userId}`, backendData);
    
    // Transform response back to frontend format
    if (response.success && response.data) {
      response.data = this.transformUserProfile(response.data);
    }
    
    return response;
  }

  /**
   * Check if email is available
   */
  async checkEmailAvailable(email: string): Promise<ApiResponse<{ available: boolean }>> {
    return httpClient.get<{ available: boolean }>(`/api/v1/users/check-email?email=${encodeURIComponent(email)}`, {
      requireAuth: false
    });
  }

  /**
   * Validate registration data
   */
  private validateRegistrationData(userData: RegisterUserData): void {
    const requiredFields = [
      'companyName',
      'firstName', 
      'lastName',
      'email',
      'agreeToTerms'
    ];

    const missingFields = requiredFields.filter(field => {
      const value = userData[field as keyof RegisterUserData];
      return value === undefined || value === null || value === '' || value === false;
    });

    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      throw new Error('Please enter a valid email address');
    }

    // Validate terms agreement
    if (!userData.agreeToTerms) {
      throw new Error('You must agree to the Terms of Service and Privacy Policy');
    }
  }

  /**
   * Get password strength score
   */
  /**
   * Get all users in organization
   */
  async getUsers(page = 1, perPage = 10, filters?: any): Promise<ApiResponse<UserProfile[]>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });

    return httpClient.get<UserProfile[]>(
      `/api/v1/users?${queryParams}`
    );
  }

  /**
   * Create a new user (admin only)
   */
  async createUser(userData: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    job_title?: string;
    department?: string;
    roles: string[];
    organization_id: string;
    send_invite?: boolean;
  }): Promise<ApiResponse<UserProfile>> {
    const { roles, ...rest } = userData;
    const primaryRole = roles[0] ?? 'viewer';

    // Backend UserCreate schema expects `role` (singular)
    const response = await httpClient.post<UserProfile>('/api/v1/users', {
      ...rest,
      role: primaryRole,
    });

    // Assign the full role list via PATCH so Keycloak groups are set correctly
    if (response.success && (response.data as any)?.user_id) {
      try {
        await httpClient.patch(`/api/v1/users/${(response.data as any).user_id}/roles`, {
          primary_role: primaryRole,
          additional_roles: roles.slice(1),
        });
      } catch (err) {
        console.warn('Failed to patch user roles after creation:', err);
      }
    }

    return response;
  }

  /**
   * Delete user (admin only)
   */
  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/users/${userId}`);
  }

  /**
   * Transform user profile from backend format to frontend format
   */
  private transformUserProfile(backendUser: any): UserProfile {
    // Handle both snake_case (backend) and camelCase (frontend) formats
    return {
      user_id: backendUser.user_id || backendUser.id,
      username: backendUser.username,
      email: backendUser.email,
      first_name: backendUser.first_name || backendUser.firstName,
      last_name: backendUser.last_name || backendUser.lastName,
      full_name: backendUser.full_name || `${backendUser.first_name || backendUser.firstName} ${backendUser.last_name || backendUser.lastName}`,
      phone: backendUser.phone,
      job_title: backendUser.job_title || backendUser.jobTitle,
      department: backendUser.department,
      hire_date: backendUser.hire_date,
      employee_id: backendUser.employee_id,
      organization_id: backendUser.organization_id || backendUser.organizationId,
      roles: backendUser.roles || [],
      permissions: backendUser.permissions || [],
      status: backendUser.status || 'active',
      emergency_contact_name: backendUser.emergency_contact_name,
      emergency_contact_phone: backendUser.emergency_contact_phone,
      emergency_contact_relationship: backendUser.emergency_contact_relationship,
      profile_picture_url: backendUser.profile_picture_url,
      timezone: backendUser.timezone,
      language: backendUser.language,
      last_login: backendUser.last_login,
      login_count: backendUser.login_count || 0,
      project_count: backendUser.project_count || 0,
      is_active: backendUser.is_active !== undefined ? backendUser.is_active : true,
      email_verified: backendUser.email_verified || false,
      two_factor_enabled: backendUser.two_factor_enabled || false,
      created_at: backendUser.created_at,
      updated_at: backendUser.updated_at,
      created_by: backendUser.created_by
    };
  }

  /**
   * Transform frontend data to backend format
   */
  private transformToBackendFormat(frontendData: Partial<UserProfile>): any {
    const backendData: any = {};
    
    // Map frontend fields to backend fields
    if (frontendData.first_name) backendData.first_name = frontendData.first_name;
    if (frontendData.last_name) backendData.last_name = frontendData.last_name;
    if (frontendData.email) backendData.email = frontendData.email;
    if (frontendData.phone) backendData.phone = frontendData.phone;
    if (frontendData.job_title) backendData.job_title = frontendData.job_title;
    if (frontendData.department) backendData.department = frontendData.department;
    if (frontendData.hire_date) backendData.hire_date = frontendData.hire_date;
    if (frontendData.employee_id) backendData.employee_id = frontendData.employee_id;
    if (frontendData.roles) backendData.roles = frontendData.roles;
    if (frontendData.permissions) backendData.permissions = frontendData.permissions;
    if (frontendData.status) backendData.status = frontendData.status;
    if (frontendData.emergency_contact_name) backendData.emergency_contact_name = frontendData.emergency_contact_name;
    if (frontendData.emergency_contact_phone) backendData.emergency_contact_phone = frontendData.emergency_contact_phone;
    if (frontendData.emergency_contact_relationship) backendData.emergency_contact_relationship = frontendData.emergency_contact_relationship;
    if (frontendData.profile_picture_url) backendData.profile_picture_url = frontendData.profile_picture_url;
    if (frontendData.timezone) backendData.timezone = frontendData.timezone;
    if (frontendData.language) backendData.language = frontendData.language;
    if (frontendData.is_active !== undefined) backendData.is_active = frontendData.is_active;
    if (frontendData.two_factor_enabled !== undefined) backendData.two_factor_enabled = frontendData.two_factor_enabled;
    
    return backendData;
  }

  getPasswordStrength(password: string): { score: number; feedback: string[] } {
    const feedback: string[] = [];
    let score = 0;

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push('Use at least 8 characters');
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include lowercase letters');
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include uppercase letters');
    }

    if (/\d/.test(password)) {
      score += 1;
    } else {
      feedback.push('Include numbers');
    }

    if (/[^a-zA-Z\d]/.test(password)) {
      score += 1;
      feedback.push('Strong password!');
    } else {
      feedback.push('Consider adding special characters');
    }

    return { score: Math.min(score, 5), feedback };
  }
}

// Export singleton instance
export const userService = new UserService();